// The phone's SIP WebSocket diagnostic for people: a plain sentence for the main flow and a compact
// technical line for "Technical details". Built from the engine's sanitized diagnostic (codes, numbers,
// scheme/host/port/path); no value is imported from the call engine, so this stays out of its lazy chunk.
import type { TransportAttempt, TransportDiagnostic, TransportLogEntry } from '@bcio-voice/call-client/core';
import type { PhoneTransportView, TransportEventView, TransportFailureView } from './bridge.ts';

/** Failures the browser reports directly. The rest are inferred from a close code the browser uses for everything. */
const OBSERVED = new Set(['socket_not_created', 'blocked_by_csp', 'connect_timeout', 'closed_by_server', 'register_rejected', 'register_timeout']);

const epoch = (iso: string | null | undefined): number | null => {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ms) ? ms : null;
};

function failureView(a: TransportAttempt | null): TransportFailureView | null {
  if (!a || !a.failure) return null;
  return {
    kind: a.failure,
    confidence: OBSERVED.has(a.failure) ? 'observed' : 'inferred',
    attempt: a.attempt,
    at: epoch(a.ended_at),
    connectMs: a.connect_ms,
    closeCode: a.close?.code ?? null,
    closeReason: a.close?.reason || null,
    wasClean: a.close?.was_clean ?? null,
    errorSeen: a.error_seen,
    socketCreated: a.socket_created,
    timedOut: a.timed_out,
    cspBlocked: a.csp_violation?.disposition === 'enforce',
    sipStatus: a.sip_status,
    code: a.failure_code,
  };
}

function eventView(e: TransportLogEntry): TransportEventView {
  return { sequence: e.sequence, at: epoch(e.at) ?? 0, kind: e.kind, attempt: e.attempt, failure: e.failure, closeCode: e.close?.code ?? null, sipStatus: e.sip_status, retryInMs: e.retry_in_ms };
}

/** Maps the engine diagnostic (absent on engines older than this field) to the bridge view. */
export function mapTransport(t: TransportDiagnostic | null | undefined): PhoneTransportView | null {
  if (!t) return null;
  const e = t.endpoint;
  return {
    endpoint: e ? `${e.scheme}://${e.host}:${e.port}${e.path}` : null,
    attempt: t.current?.attempt ?? 0,
    state: t.current?.outcome ?? null,
    lastFailure: failureView(t.last_failure),
    consecutiveFailures: t.consecutive_failures,
    nextRetryAt: t.next_retry_at,
    retryDelayMs: t.retry_delay_ms,
    recent: t.recent.map(eventView),
  };
}

const FAILURE_TEXT: Record<string, string> = {
  socket_not_created: 'this browser refused to open the phone connection',
  blocked_by_csp: 'this page’s security policy blocked the phone connection',
  connect_timeout: 'the phone system didn’t answer in time',
  handshake_failed: 'the connection was closed before it opened (the browser doesn’t say why)',
  closed_by_server: 'the phone system closed the connection',
  connection_lost: 'the connection was cut off',
  register_rejected: 'the phone system didn’t accept this browser',
  register_timeout: 'the phone system didn’t answer the registration',
  register_failed: 'the registration couldn’t be sent',
  transport_error: 'the connection failed',
};

/** What went wrong, as a clause ("the connection was cut off"), or null when unknown. */
export function failureClause(f: Pick<TransportFailureView, 'kind' | 'sipStatus'> | null | undefined): string | null {
  if (!f) return null;
  if (f.kind === 'register_rejected' && (f.sipStatus === 401 || f.sipStatus === 403)) return 'the phone system didn’t accept this browser’s credentials';
  return FAILURE_TEXT[f.kind] ?? null;
}

/** "The last try failed: …" while the phone is not connected and a failure explains it. */
export function lastFailureSentence(t: PhoneTransportView | null | undefined): string | null {
  if (!t || t.state === 'registered' || t.consecutiveFailures === 0) return null;
  const clause = failureClause(t.lastFailure);
  return clause ? `The last try failed: ${clause}.` : null;
}

/** One line for "Technical details". Codes and the sanitized endpoint only. */
export function transportDetail(t: PhoneTransportView | null | undefined): string | null {
  if (!t) return null;
  const parts: string[] = [];
  if (t.endpoint) parts.push(t.endpoint);
  const f = t.lastFailure;
  if (f && t.consecutiveFailures > 0) {
    parts.push(`try ${f.attempt} failed${f.connectMs !== null ? ` after ${f.connectMs} ms` : ''}: ${f.kind} (${f.confidence})`);
    if (f.closeCode !== null) parts.push(`WebSocket close ${f.closeCode}${f.wasClean ? ', clean' : ', not clean'}${f.closeReason ? `, “${f.closeReason}”` : ''}`);
    if (f.socketCreated === false) parts.push('socket not created');
    if (f.errorSeen) parts.push('error event');
    if (f.timedOut) parts.push('connect timeout');
    parts.push(f.cspBlocked ? 'blocked by connect-src' : 'no connect-src block seen');
    if (f.sipStatus !== null) parts.push(`SIP ${f.sipStatus}`);
    if (f.code) parts.push(f.code);
    parts.push(`${t.consecutiveFailures} failed ${t.consecutiveFailures === 1 ? 'try' : 'tries'} in a row`);
  }
  if (t.nextRetryAt && t.state !== 'registered') parts.push(`next try ${t.nextRetryAt}`);
  return parts.length ? parts.join('; ') : null;
}
